import { useState } from 'react'
import { useResetPassword } from '@/v2/hooks/auth/useAuthentication'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { useNavigate, useLocation } from 'react-router-dom'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'

const resetPasswordSchema = yup.object({
  password: yup
    .string()
    .min(6, 'Password must be at least 6 characters long')
    .required('Password is required'),
  password_confirmation: yup
    .string()
    .oneOf([yup.ref('password')], "Passwords don't match")
    .required('Password confirmation is required'),
})

export const useResetPasswordForm = () => {
  const { search } = useLocation()
  const navigate = useNavigate()
  const { resetPassword, isLoading } = useResetPassword()
  const { onOpenSnackbar } = useSnackbarContext()

  const [values, setValues] = useState({
    password: '',
    password_confirmation: '',
  })
  const [errors, setErrors] = useState({})

  const {
    register,
    formState: { isSubmitting },
  } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(resetPasswordSchema),
    defaultValues: values,
  })

  const handleResetPassword = async () => {
    const [reset_password_token] = search.split('=').slice(-1)
    const resetPwdBody = {
      session: {
        reset_password_token,
        password: values.password,
        password_confirmation: values.password_confirmation,
      },
    }
    await resetPassword(resetPwdBody, {
      onSuccess: data => {
        navigate('/v2/login')
        onOpenSnackbar(data.message, 'success')
      },
      onError: error => {
        onOpenSnackbar(`${error}`, 'error')
      },
    })
  }

  const handleSubmitForm = async () => {
    try {
      await resetPasswordSchema.validate(values, { abortEarly: false })
      setErrors({})
      await handleResetPassword()
    } catch (validationError) {
      const fieldErrors = {}
      if (validationError.inner && validationError.inner.length > 0) {
        validationError.inner.forEach(err => {
          if (!fieldErrors[err.path]) {
            fieldErrors[err.path] = err.message
          }
        })
      } else {
        fieldErrors[validationError.path] = validationError.message
      }
      setErrors(fieldErrors)
    }
  }

  return {
    values,
    errors,
    isLoading,
    isSubmitting,
    register,
    setValues,
    handleSubmitForm,
  }
}
